import React, { useMemo, useState } from 'react';
import {
  Box,
  Flex,
  Text,
  Input,
  Select,
  SimpleGrid,
  Button,
  InputGroup,
  InputLeftElement,
} from '@chakra-ui/react';
import { MagnifyingGlass, UsersThree } from 'phosphor-react';
import { IPessoalEmpregado } from '../../types';
import { UI } from '../../ui';
import { CollabCard } from './CollabCard';
import { statusOf, CollabStatus } from './CollabAvatar';
import { ARCHETYPES, classifyCargo, genderKey, GenderKey } from './jobArchetypes';

const PAGE = 48;

const GENDER_LABEL: Record<GenderKey, string> = {
  M: 'Masculino',
  F: 'Feminino',
  N: 'Não informado',
};
const STATUS_OPTS: { v: CollabStatus | ''; label: string }[] = [
  { v: '', label: 'Todos os status' },
  { v: 'ativo', label: 'Ativos' },
  { v: 'demitido', label: 'Demitidos' },
  { v: 'afastado', label: 'Afastados' },
];

type GroupBy = 'none' | 'arquetipo' | 'departamento';

interface EquipeGalleryProps {
  empregados: IPessoalEmpregado[];
  onSelect: (emp: IPessoalEmpregado) => void;
}

export function EquipeGallery({ empregados, onSelect }: EquipeGalleryProps) {
  const [busca, setBusca] = useState('');
  const [fStatus, setFStatus] = useState<CollabStatus | ''>('');
  const [fDepto, setFDepto] = useState('');
  const [fGenero, setFGenero] = useState<GenderKey | ''>('');
  const [fArch, setFArch] = useState('');
  const [group, setGroup] = useState<GroupBy>('none');
  const [limite, setLimite] = useState(PAGE);

  const deptos = useMemo(
    () =>
      Array.from(
        new Set(empregados.map(e => (e.departamento || '').trim()).filter(Boolean)),
      ).sort(),
    [empregados],
  );
  const archsPresentes = useMemo(() => {
    const keys = new Set(empregados.map(e => classifyCargo(e.cargo).key));
    return ARCHETYPES.filter(a => keys.has(a.key));
  }, [empregados]);

  const filtrados = useMemo(() => {
    const q = busca.trim().toLowerCase();
    return empregados.filter(e => {
      if (q && !`${e.nome} ${e.cargo}`.toLowerCase().includes(q)) return false;
      if (fStatus && statusOf(e.status, e.situacao === 'Afastado') !== fStatus) return false;
      if (fDepto && (e.departamento || '').trim() !== fDepto) return false;
      if (fGenero && genderKey(e.sexo) !== fGenero) return false;
      if (fArch && classifyCargo(e.cargo).key !== fArch) return false;
      return true;
    });
  }, [empregados, busca, fStatus, fDepto, fGenero, fArch]);

  const visiveis = filtrados.slice(0, limite);

  // Agrupamento (sobre os visíveis, p/ manter o "carregar mais").
  const grupos = useMemo(() => {
    if (group === 'none') return [{ titulo: '', itens: visiveis }];
    const map = new Map<string, IPessoalEmpregado[]>();
    visiveis.forEach(e => {
      const k =
        group === 'arquetipo'
          ? classifyCargo(e.cargo).label
          : (e.departamento || 'Sem departamento').trim();
      const arr = map.get(k) ?? [];
      arr.push(e);
      map.set(k, arr);
    });
    return Array.from(map.entries())
      .sort((a, b) => b[1].length - a[1].length)
      .map(([titulo, itens]) => ({ titulo, itens }));
  }, [group, visiveis]);

  const selectSx = { size: 'sm' as const, borderRadius: '10px', bg: 'white' };

  return (
    <Box>
      {/* Barra de filtros */}
      <Flex gap={2.5} align="center" wrap="wrap" mb={5}>
        <InputGroup size="sm" w={{ base: '100%', md: '260px' }}>
          <InputLeftElement pointerEvents="none" color={UI.muted}>
            <MagnifyingGlass size={15} />
          </InputLeftElement>
          <Input
            placeholder="Buscar por nome ou cargo…"
            value={busca}
            onChange={e => {
              setBusca(e.target.value);
              setLimite(PAGE);
            }}
            borderRadius="10px"
            bg="white"
          />
        </InputGroup>

        <Select
          {...selectSx}
          w="auto"
          value={fStatus}
          onChange={e => {
            setFStatus(e.target.value as CollabStatus | '');
            setLimite(PAGE);
          }}
        >
          {STATUS_OPTS.map(o => (
            <option key={o.v} value={o.v}>
              {o.label}
            </option>
          ))}
        </Select>

        <Select
          {...selectSx}
          w="auto"
          maxW="190px"
          value={fDepto}
          onChange={e => {
            setFDepto(e.target.value);
            setLimite(PAGE);
          }}
        >
          <option value="">Todos os deptos</option>
          {deptos.map(d => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </Select>

        <Select
          {...selectSx}
          w="auto"
          value={fGenero}
          onChange={e => {
            setFGenero(e.target.value as GenderKey | '');
            setLimite(PAGE);
          }}
        >
          <option value="">Todos os gêneros</option>
          {(['M', 'F', 'N'] as GenderKey[]).map(g => (
            <option key={g} value={g}>
              {GENDER_LABEL[g]}
            </option>
          ))}
        </Select>

        <Select
          {...selectSx}
          w="auto"
          maxW="180px"
          value={fArch}
          onChange={e => {
            setFArch(e.target.value);
            setLimite(PAGE);
          }}
        >
          <option value="">Todos os trabalhos</option>
          {archsPresentes.map(a => (
            <option key={a.key} value={a.key}>
              {a.label}
            </option>
          ))}
        </Select>

        <Box flex={1} />

        <Flex bg={UI.neutralSoft} borderRadius="10px" p="3px" gap="2px">
          {(
            [
              ['none', 'Sem grupo'],
              ['arquetipo', 'Trabalho'],
              ['departamento', 'Depto'],
            ] as [GroupBy, string][]
          ).map(([k, lbl]) => (
            <Button
              key={k}
              size="xs"
              variant="unstyled"
              px={3}
              h="28px"
              borderRadius="8px"
              fontWeight={700}
              bg={group === k ? UI.card : 'transparent'}
              color={group === k ? UI.brand : UI.muted}
              boxShadow={group === k ? '0 1px 4px rgba(15,23,42,.08)' : 'none'}
              onClick={() => setGroup(k)}
            >
              {lbl}
            </Button>
          ))}
        </Flex>
      </Flex>

      <Text fontSize="12px" color={UI.muted} mb={4}>
        {filtrados.length} {filtrados.length === 1 ? 'colaborador' : 'colaboradores'}
        {filtrados.length > visiveis.length ? ` · exibindo ${visiveis.length}` : ''}
      </Text>

      {filtrados.length === 0 && (
        <Flex direction="column" align="center" justify="center" py={16} color={UI.muted} gap={3}>
          <UsersThree size={40} />
          <Text>Nenhum colaborador com esses filtros.</Text>
        </Flex>
      )}

      {grupos.map(g => (
        <Box key={g.titulo || 'all'} mb={g.titulo ? 7 : 0}>
          {g.titulo && (
            <Flex align="center" gap={2.5} mb={3}>
              <Text
                fontSize="11px"
                fontWeight={800}
                color={UI.muted}
                textTransform="uppercase"
                letterSpacing="0.1em"
              >
                {g.titulo}
              </Text>
              <Box
                bg={`${UI.brand}14`}
                color={UI.brand}
                px={2}
                borderRadius="full"
                fontSize="11px"
                fontWeight={800}
              >
                {g.itens.length}
              </Box>
              <Box flex="1" h="1px" bg="rgba(15,23,42,0.06)" />
            </Flex>
          )}
          <SimpleGrid columns={{ base: 1, sm: 2, lg: 3, '2xl': 4 }} spacing={4}>
            {g.itens.map((e, i) => (
              <CollabCard
                key={e.empregado_id}
                emp={e}
                afastado={e.situacao === 'Afastado'}
                index={i}
                onSelect={onSelect}
              />
            ))}
          </SimpleGrid>
        </Box>
      ))}

      {filtrados.length > visiveis.length && (
        <Flex justify="center" mt={6}>
          <Button
            size="sm"
            variant="outline"
            borderRadius="full"
            px={6}
            onClick={() => setLimite(l => l + PAGE)}
          >
            Carregar mais ({filtrados.length - visiveis.length} restantes)
          </Button>
        </Flex>
      )}
    </Box>
  );
}
